import { NowImporting } from "@/components/molecules/NowImporting/NowImporting";
import SBCFiltersSidebar from "@/components/organisms/SBCFiltersSidebar/SBCFiltersSidebar";
import { SetHeader } from "@/components/organisms/SBCs/SbcSetHeader/SetHeader";
import { useInitImport } from "@/hooks/useInitImport";
import GetCardSBC from "@/pages/Sbcs/SbcSetPage/components/GetCardSbc";
import SbcSetDesktopView from "@/pages/Sbcs/SbcSetPage/views/SbcSetDesktopView";
import SbcSetMobileView from "@/pages/Sbcs/SbcSetPage/views/SbcSetMobileView";
import { setIsCurrentlyImporting } from "@/redux/globalState/globalStateSlice";
import { showModal } from "@/redux/message/messageSlice";
import {
  fetchSbcSet,
  fetchSbcs,
  getSbcSetSelector,
  getSbcsSelector,
} from "@/redux/sbcs/sbcsSlice";
import { AppDispatch } from "@/redux/store";
import {
  canUseClubImportSelector,
  getUserSelector,
} from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import {
  handleCompleteToggle,
  handleFavoriteToggle,
  handleHideToggle,
  handleVote,
} from "./handlers/sbcSetHandlers";

export function formatSbcSetName(name?: string): string {
  if (!name) return "...";
  return name
    .toLowerCase()
    .replace(/-/g, " ")
    .normalize("NFD")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const SbcSetPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { sbcs, sbcSetStatus } = useSelector(getSbcsSelector);
  const { setId, name } = useParams();
  const sbcSet = useSelector(getSbcSetSelector(+setId));
  const isMobile = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const [showFiltersBar, setShowFiltersBar] = useState(false);
  const navigate = useNavigate();
  const { user } = useSelector(getUserSelector);
  const initImport = useInitImport({ user });
  const [showImporting, setShowImporting] = useState(false);
  const canUseClubImport = useSelector(canUseClubImportSelector);

  useEffect(() => {
    if (!sbcSet) dispatch(fetchSbcSet({ setId: +setId }));
  }, [sbcSet, dispatch, setId]);

  useEffect(() => {
    if (sbcSetStatus === "rejected") navigate("/");
    window.scrollTo(0, 0);
  }, [sbcSetStatus, navigate]);

  useEffect(() => {
    dispatch(fetchSbcs({ setId: +setId }));
  }, [dispatch, setId]);

  const toTitleCase = (str: string) =>
    str
      .replaceAll("-", " ")
      .replace(/\b[a-z]/g, (match) => match.toUpperCase());

  const onClickSetToggle = (toggleType) => {
    if (!user.uuid) {
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook:
            toggleType === "completed"
              ? "complete-sbc"
              : toggleType === "favorite"
                ? "favorite-sbc"
                : "hide-sbc",
        }),
      );
      return;
    }

    if (toggleType === "favorite") handleFavoriteToggle(dispatch, sbcSet);
    if (toggleType === "hidden") handleHideToggle(dispatch, sbcSet);
    if (toggleType === "completed") handleCompleteToggle(dispatch, sbcSet);
  };

  const sbcFiltersSidebar = (
    <SBCFiltersSidebar
      isUsingClubImport={false}
      onHandleImportClicked={() =>
        initImport().then(() => {
          dispatch(setIsCurrentlyImporting(true));
          setTimeout(() => setShowImporting(true), 1000);
          window.open(
            "https://www.ea.com/ea-sports-fc/ultimate-team/web-app/",
            "_blank",
          );
        })
      }
    />
  );

  const setHeader = (
    <SetHeader
      sbcSet={sbcSet}
      platform={user.platform}
      onClickSbcToggle={onClickSetToggle}
      onVote={(vote) => handleVote(dispatch, sbcSet, vote, user.uuid)}
      isOnSbcPage={true}
    />
  );

  const sbcsCount = sbcSet?.sbcsCount ?? 0;
  const solvedCount = sbcSet?.solvedCount ?? 0;

  return (
    <main className="min-h-[calc(100vh+112px)] flex justify-center overflow-x-hidden">
      {showImporting && (
        <NowImporting
          onClickDone={() => setShowImporting(false)}
          onClickCancel={() => setShowImporting(false)}
        />
      )}
      <Helmet>
        <title>SBC Solution - {toTitleCase(name)}</title>
        <link
          rel="canonical"
          href={`https://www.easysbc.io/sbc-solution/${name}/${setId}`}
        />
        <meta
          name="description"
          content={sbcSet?.description || `SBC Solution - ${toTitleCase(name)}`}
        />
      </Helmet>
      <div className="w-full flex flex-col bg-panel-base">
        {isMobile ? (
          <SbcSetMobileView
            sbcSet={sbcSet}
            setHeader={setHeader}
            solvedCount={solvedCount}
            sbcsCount={sbcsCount}
            user={user}
            showFiltersBar={showFiltersBar}
            setShowFiltersBar={setShowFiltersBar}
            sbcs={sbcs}
            getCardSBC={sbcs.map((sbc, index) => (
              <GetCardSBC key={sbc.id} sbc={sbc} index={index} />
            ))}
            sbcFiltersSidebar={sbcFiltersSidebar}
            showImporting={showImporting}
            setShowImporting={setShowImporting}
            name={name}
            id={setId}
            onClickSetToggle={(type) => {
              if (!canUseClubImport) {
                onClickSetToggle(type);
              } else {
                dispatch(
                  showModal({
                    modalType: "premium-feature",
                    premiumFeatureHook: "solve-sbc-with-club",
                  }),
                );
              }
            }}
          />
        ) : (
          <SbcSetDesktopView
            sbcSet={sbcSet}
            setHeader={setHeader}
            sbcs={sbcs}
            getCardSBC={sbcs.map((sbc, index) => (
              <GetCardSBC key={sbc.id} sbc={sbc} index={index} />
            ))}
            sbcFiltersSidebar={sbcFiltersSidebar}
            name={name}
          />
        )}
      </div>
    </main>
  );
};

export default SbcSetPage;
